import { Edit, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { DataListCard, type DataListColumn, type DataListAction } from '@/components/common/DataListCard';
import type { SubcontractorDto } from '../types';

interface SubcontractorListProps {
  subcontractors: SubcontractorDto[];
  onEdit: (subcontractor: SubcontractorDto) => void;
  onDeactivate: (subcontractor: SubcontractorDto) => void;
  isLoading?: boolean;
  canEdit?: boolean;
}

export function SubcontractorList({
  subcontractors,
  onEdit,
  onDeactivate,
  isLoading = false,
  canEdit = false,
}: SubcontractorListProps) {
  const columns: DataListColumn<SubcontractorDto>[] = [
    {
      key: 'companyName',
      label: 'Company Name',
      render: (sub) => sub.companyName,
    },
    {
      key: 'contactName',
      label: 'Contact Name',
      render: (sub) => sub.contactName || '-',
    },
    {
      key: 'phone',
      label: 'Phone',
      render: (sub) => sub.officePhone || sub.cellPhone || '-',
    },
    {
      key: 'email',
      label: 'Email',
      render: (sub) => sub.email || '-',
    },
    {
      key: 'commission',
      label: 'Commission %',
      render: (sub) => `${sub.defaultCommissionPercent}%`,
    },
    {
      key: 'status',
      label: 'Status',
      render: (sub) => (
        <Badge variant={sub.isActive ? 'default' : 'secondary'}>
          {sub.isActive ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
  ];

  const actions: DataListAction<SubcontractorDto>[] = [
    {
      label: '',
      icon: <Edit className="h-4 w-4" />,
      onClick: onEdit,
      title: 'Edit subcontractor',
      canShow: () => canEdit,
    },
    {
      label: '',
      icon: <Trash2 className="h-4 w-4 text-red-500" />,
      onClick: onDeactivate,
      canShow: (sub) => canEdit && sub.isActive,
      title: 'Deactivate subcontractor',
    },
  ];

  return (
    <DataListCard
      items={subcontractors}
      columns={columns}
      actions={actions}
      keyExtractor={(sub) => sub.id}
      isLoading={isLoading}
      loadingMessage="Loading subcontractors..."
      emptyMessage="No subcontractors found."
    />
  );
}
